/**
 * @fileoverview Modal component for displaying detailed personal investment information
 * @active This component is actively used in the person details view
 *
 * @see {@link src/components/personBy_Id/overview/PersonalInvestments/PersonalInvestments.tsx}
 * @see {@link src/utils/formatDate.ts}
 * @see {@link src/types/joins/companyFundraisingInvestors.ts} - Type definitions for personal investments
 */

import { h_400_12_135_48, h_700_16_100_32 } from "@/styles/fontStyles";
import type { PersonalInvestmentDetail } from "@/types/joins/companyFundraisingInvestors";
import {
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import CloseModalSvg from "@/assets/svg/close-modal.svg";
import { formatDateTimeFromDB, formatValues } from "@/utils/formatDate";
import { formatNumber } from "@/utils/formatNumber";
import { PlainTextInTable } from "../AssociatedCompanies/AssociatedCompanies";
import { imgSelector } from "@/utils/imgSelector";
import DefaultImage from "@/components/ui/DefaultImage";

/**
 * Interface for the Modal control props
 * @interface ModalControlProps
 */
interface ModalControlProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
}

/**
 * Interface for the PersonalInvestmentsModal component props
 * @interface PersonalInvestmentsModalProps
 */
interface PersonalInvestmentsModalProps {
  /** Modal control props for opening/closing */
  control: ModalControlProps;
  /** Investment data to display in the modal */
  data: PersonalInvestmentDetail;
}

/**
 * Modal component for displaying detailed personal investment information
 * @param {PersonalInvestmentsModalProps} props - Component props
 * @returns {JSX.Element} Modal component
 */
export const PersonalInvestmentsModal = ({
  control,
  data,
}: PersonalInvestmentsModalProps): JSX.Element => {
  const { isOpen, onClose } = control;

  const renderData = [
    {
      title: "Stage",
      value: data?.fundraiseTransaction?.round,
      width: "156px",
    },
    {
      title: "Type",
      value: data?.fundraiseTransaction?.roundType,
      width: "48px",
    },
    {
      title: "Status",
      value: data?.fundraiseTransaction?.status,
      width: "49px",
    },
    {
      title: "Date",
      value: data?.fundraiseTransaction?.dateAnnounced
        ? formatDateTimeFromDB(
            data.fundraiseTransaction.dateAnnounced,
            formatValues.short
          )
        : "",
      width: "80px",
    },
    {
      title: "Amount Raised",
      value:
        data?.fundraiseTransaction?.amountRaised !== undefined
          ? formatNumber(data.fundraiseTransaction.amountRaised)
          : "",
      width: "70px",
    },
    {
      title: "Total Investors",
      value: data?.fundraiseTransaction?.numberOfInvestors,
      width: "auto",
    },
  ];

  /**
   * Renders each data item with proper styling
   * @param {{ item: { value: any, title: string, width: string } }} params - Item to render
   * @returns {JSX.Element} Rendered block component
   */
  const blockSwitcher = ({
    item,
  }: {
    item: { value: any; title: string; width: string };
  }): JSX.Element => {
    return (
      <PlainTextInTable
        {...{ data: item?.value, styleText: { color: "mainBlue" } }}
      />
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent w={"327px"} p={"10px 20px 20px 20px"}>
        <ModalBody p={"0px"}>
          <Flex flexDir={"column"} gap={"16px"}>
            <Flex justify={"space-between"} align={"center"}>
              <Flex gap={"8px"} align={"center"}>
                <DefaultImage
                  src={imgSelector({ data: data?.fundraiseTransaction || {} })}
                  options={{
                    borderRadius: "2px",
                    w: "22px",
                  }}
                />
                <Text {...h_700_16_100_32} color={"naturalBlack"} pt={"4px"}>
                  {data?.fundraiseTransaction?.nameBrand}
                </Text>
              </Flex>
              <Flex
                align={"center"}
                onClick={() => {
                  onClose();
                }}
                cursor={"pointer"}
              >
                <CloseModalSvg />
              </Flex>
            </Flex>
            <Flex flexWrap={"wrap"} justify={"space-between"} gap={"8px"}>
              {data &&
                renderData?.map((item) => {
                  return (
                    <Flex
                      flexDir={"column"}
                      key={"invest" + item?.title}
                      w={item?.width}
                      gap={"4px"}
                    >
                      <Text
                        {...h_400_12_135_48}
                        color={"gray"}
                        whiteSpace={"nowrap"}
                      >
                        {item?.title}
                      </Text>
                      {blockSwitcher({ item })}
                    </Flex>
                  );
                })}
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
